import { Accordion, Form, Badge, Stack, Image, Row, Col } from 'react-bootstrap';
import { IMAGE_BASE_URL, IMAGE_URL } from '../../../config';
import { useDispatch, useSelector } from 'react-redux';
import MainCard from '../../../components/MainCard';
import { updateItemStatusLocally } from '../../../store/slices/location/locationSlice';
import { toggleFoodMenu } from '../../../store/slices/location/locationThunk';
import { memo, useMemo, useState, useEffect, useRef } from 'react'; // Added useRef
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { IoSearch } from 'react-icons/io5';

export const MenuLocationCard = memo(({ locationId }) => {
  const { foodMenu: menuData } = useSelector((store) => store.location);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const [search, setSearch] = useState('');
  const [activeKeys, setActiveKeys] = useState([]); 
  const prevSearchRef = useRef(''); // Track search string to prevent toggle-collapse

  const filteredCategory = useMemo(() => {
    if (!search) return menuData;
    const searchTerm = search.toLowerCase();

    return menuData
      .map((category) => {
        const isCategoryMatch = category.name.toLowerCase().includes(searchTerm);
        const matchingItems = category.foodItems.filter((item) =>
          item.name.toLowerCase().includes(searchTerm)
        );

        if (isCategoryMatch || matchingItems.length > 0) {
          return {
            ...category,
            foodItems: isCategoryMatch ? category.foodItems : matchingItems 
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [menuData, search]);

  // FIX: Only update active keys when search text changes
  useEffect(() => {
    if (search !== prevSearchRef.current) {
      if (search.length > 0) {
        const keys = filteredCategory.map((cat) => String(cat.id));
        setActiveKeys(keys);
      } else {
        setActiveKeys([]); 
      }
      prevSearchRef.current = search;
    }
  }, [search, filteredCategory]);

  const handleItemToggle = async (itemId, currentStatus) => {
    const newStatus = !currentStatus;
    dispatch(updateItemStatusLocally({ itemId, isAvailable: newStatus }));
    try {
      await dispatch(
        toggleFoodMenu({ foodItemId: itemId, locationId: Number(locationId), isAvailable: newStatus, skipLoader: true })
      ).unwrap();
    } catch (error) {
      dispatch(updateItemStatusLocally({ itemId, isAvailable: currentStatus }));
      toast.error('Failed to update status. Reverting change.');
    }
  };

  return (
    <>
      <div className="position-relative mb-3" style={{ maxWidth: '300px' }}>
        <IoSearch size={20} className="position-absolute top-50 translate-middle-y text-muted" style={{ left: '15px' }} />
        <Form.Control
          onChange={(e) => setSearch(e.target.value)}
          type="text"
          placeholder="Search category or item..."
          style={{ paddingLeft: '40px' }}
        />
      </div>

      <Accordion 
        alwaysOpen 
        activeKey={activeKeys} 
        onSelect={(k) => setActiveKeys(k)} 
        className="custom-accordion"
      >
        {filteredCategory.map((category) => (
          <MainCard key={category.id} content={false} className="mb-4">
            <Accordion.Item eventKey={String(category.id)} className="border-0">
              <Accordion.Header>
                <Stack direction="horizontal" gap={3} className="w-100 pe-3">
                  <div className="rounded" style={{ width: '4px', height: '24px', backgroundColor: 'var(--pc-sidebar-active-color)' }} />
                  <div className="flex-grow-1">
                    <h5 className="mb-0">{category.name}</h5>
                    <small className="text-muted">
                      {category.foodItems.length} {t('common.foodItemsAvailable')}
                    </small>
                  </div>
                </Stack>
              </Accordion.Header>

              <Accordion.Body className="bg-light border-top">
                <Row className="g-3">
                  {category.foodItems.map((item) => (
                    <Col key={item.id} xs={12} md={6} lg={6}>
                      <div className="d-flex align-items-center justify-content-between bg-white p-3 rounded shadow-sm border">
                        <Stack direction="horizontal" gap={3}>
                          <Image
                            src={`${IMAGE_BASE_URL}${item.image}`}
                            rounded
                            style={{ width: '60px', height: '60px', objectFit: 'cover', border: '1px solid #eee' }}
                            onError={(e) => (e.target.src = 'https://via.placeholder.com/60?text=Food')}
                          />
                          <div>
                            <div className="d-flex align-items-center">
                               <img
                                style={{ width: '16px', height: '16px', marginRight: '5px' }}
                                src={item.foodDietary === 'Vegetarian' ? `${IMAGE_URL}/veg.jpg` : `${IMAGE_URL}/no-veg.png`}
                                alt=""
                              />
                              <div className="fw-bold mb-0 text-dark">{item.name}</div>
                            </div>
                            <div className="fw-bold mt-2" style={{ color: 'var(--pc-sidebar-active-color)' }}>
                              ${item.basePrice}
                            </div>
                          </div>
                        </Stack>

                        <div className="menu-switch d-flex align-items-center text-end">
                            <span style={{userSelect:'none'}} className={`small fw-bold ${item.isAvailable ? 'text-dark' : 'text-muted'}`}>
                                  {item.isAvailable ? t('common.available') : t('common.disabled')}
                                </span>
                          <Form.Check
                            type="switch"
                            id={`item-switch-${item.id}`}
                            checked={item.isAvailable}
                            style={{ cursor: 'pointer' }}
                            onChange={() => handleItemToggle(item.id, item.isAvailable)}
                          />
                        </div>
                      </div>
                    </Col>
                  ))}
                  {category.foodItems.length === 0 && (
                    <Col>
                      <div className="p-4 text-center text-muted ">{t('common.noItemsFoundInThisSection')}</div>
                    </Col>
                  ) }
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </MainCard>
        ))}

        {filteredCategory.length === 0 && (
          <div className="p-4 text-center text-muted ">{t('common.noItemsFoundInThisSection')}</div>
        )}  
      </Accordion>
    </>
  );
});